import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckSquare, Square, Loader2, Flag, Download, KeyRound, Fingerprint, BookOpen, FileCheck2, FileText } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

// The estimator's checklist: every requested change must be cleared before the bid can be resubmitted.
export function FlagChecklist({ approval, onChange }) {
  const [busyId, setBusyId] = useState(null);
  const flags = approval?.flags || [];
  if (!flags.length) return null;
  const clear = async (f) => {
    const note = window.prompt(`What did you change for “${f.label}”? (optional)`) ?? null;
    if (note === null) return;
    setBusyId(f.flag_id);
    try { await api.post(`/approvals/${approval.approval_id}/flags/${f.flag_id}/clear`, { note }); toast.success("Flag cleared"); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not clear"); }
    finally { setBusyId(null); }
  };
  const open = flags.filter((f) => !f.cleared_at).length;
  return (
    <div className="mt-2 rounded-lg border border-rose-200 bg-white divide-y divide-rose-100" data-testid="approval-flag-checklist">
      <div className="px-3 py-1.5 text-[11px] font-semibold text-rose-700 flex items-center gap-1.5"><Flag className="w-3 h-3" /> Requested changes · {open ? `${open} open` : "all cleared — resubmit when ready"}</div>
      {flags.map((f) => (
        <div key={f.flag_id} className="px-3 py-2 flex items-start gap-2 text-xs" data-testid={`approval-flag-${f.flag_id}`}>
          <button onClick={() => !f.cleared_at && clear(f)} disabled={!!f.cleared_at || busyId === f.flag_id} data-testid={`approval-flag-clear-${f.flag_id}`} className="mt-0.5 text-slate-500 hover:text-emerald-600 disabled:opacity-100">
            {busyId === f.flag_id ? <Loader2 className="w-4 h-4 animate-spin" /> : f.cleared_at ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
          </button>
          <div className="min-w-0 flex-1">
            <div className={`font-medium ${f.cleared_at ? "line-through text-slate-400" : "text-slate-800"}`}>{f.label}{f.page ? ` · p.${f.page}` : ""} <span className="text-slate-400 font-normal">· {f.kind_label}</span></div>
            <div className={f.cleared_at ? "text-slate-400" : "text-slate-600"}>{f.text}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{f.by_name} · {fmt(f.at)} · {f.key}{f.signature_key ? ` · ${f.signature_key}` : ""}{f.cleared_at ? ` · cleared by ${f.cleared_by_name} ${fmt(f.cleared_at)}${f.cleared_note ? ` — “${f.cleared_note}”` : ""}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// What an approval left behind: who, when, the total, the fingerprint, the key and the stamped files.
export function ApprovalMark({ approval }) {
  const ap = approval?.approval;
  if (!ap) return null;
  const file = (which) => `${process.env.REACT_APP_BACKEND_URL}/api/approvals/${approval.approval_id}/file/${which}`;
  const files = ap.files || {};
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-emerald-800/90" data-testid="approval-mark">
      <span className="inline-flex items-center gap-1 font-mono font-semibold" data-testid="approval-key"><KeyRound className="w-3 h-3" /> {ap.key}</span>
      <span>{ap.by_name}{ap.role_label ? `, ${ap.role_label}` : ""} · {ap.at_local || fmt(ap.at)}</span>
      {ap.fingerprint && <span className="inline-flex items-center gap-1 font-mono" title={ap.fingerprint} data-testid="approval-fingerprint"><Fingerprint className="w-3 h-3" /> {ap.fingerprint.slice(0, 12)}…</span>}
      {files.proposal_stamped && <a href={file("proposal_stamped")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline" data-testid="approval-file-proposal"><FileCheck2 className="w-3 h-3" /> Stamped proposal</a>}
      {files.estimate_stamped && <a href={file("estimate_stamped")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline" data-testid="approval-file-estimate"><Download className="w-3 h-3" /> Stamped estimate</a>}
      {ap.stamp_error && <span className="text-amber-700">Stamp not placed: {ap.stamp_error}</span>}
      {ap.voided_at && <span className="text-rose-700 font-semibold">Voided {fmt(ap.voided_at)}</span>}
    </div>
  );
}

const EVENT_CLS = { submitted: "text-slate-700", resubmitted: "text-slate-700", flag: "text-rose-700", "flag cleared": "text-emerald-700", denied: "text-rose-700",
  approved: "text-emerald-700", voided: "text-amber-700", override: "text-indigo-700", cancelled: "text-slate-400", "step approved": "text-emerald-700" };

// Every submit, flag, deny, approve, void and override on the bid — exportable.
export function ApprovalLedger({ bidId, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => { if (open) api.get(`/bids/${bidId}/approvals/ledger`).then((r) => setRows(r.data.rows)).catch(() => setRows([])); }, [open, bidId, refreshKey]);
  return (
    <div className="px-5" data-testid="approval-ledger">
      <button onClick={() => setOpen((o) => !o)} data-testid="approval-ledger-toggle" className="text-[11px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 py-1"><BookOpen className="w-3 h-3" /> {open ? "Hide" : "Show"} approvals ledger</button>
      {open && (
        <div className="mb-2 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100">
            <span className="text-[11px] font-semibold text-slate-700">{rows ? `${rows.length} entries` : "Loading…"}</span>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/bids/${bidId}/approvals/ledger?fmt=csv`} className="text-[11px] text-[#003E87] inline-flex items-center gap-1 hover:underline" data-testid="approval-ledger-export"><Download className="w-3 h-3" /> Export CSV</a>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
            {(rows || []).map((r, i) => (
              <div key={i} className="px-3 py-1.5 text-[11px] grid grid-cols-[110px_90px_1fr] gap-2 items-baseline" data-testid="approval-ledger-row">
                <span className="text-slate-400">{fmt(r.at)}</span>
                <span className={`font-semibold uppercase tracking-wide ${EVENT_CLS[r.event] || "text-slate-600"}`}>{r.event}</span>
                <span className="text-slate-700 min-w-0 truncate" title={r.detail}>{r.by}{r.key ? <span className="font-mono text-slate-500"> · {r.key}</span> : ""}{r.fingerprint ? <span className="font-mono text-slate-400"> · {r.fingerprint.slice(0, 10)}…</span> : ""}{r.detail ? ` — ${r.detail}` : ""}</span>
              </div>
            ))}
            {rows && rows.length === 0 && <div className="px-3 py-3 text-[11px] text-slate-400">Nothing yet — the ledger starts with the first submit.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// The internal executive summary behind a sign-off request: headline facts + the signed-in PDF. Never a client path.
export function ProposalReadLink({ approval }) {
  if (!approval || approval.trigger !== "send" || approval.has_proposal === false) return null;
  if (approval.kind === "portfolio") return <PacketList approval={approval} />;
  const href = `${process.env.REACT_APP_BACKEND_URL}/api/approvals/${approval.approval_id}/proposal`;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#003E87] hover:bg-[#00305F] rounded-md px-2.5 py-1.5" data-testid="approval-read-proposal">
      <FileText className="w-3.5 h-3.5" /> Read the proposal (PDF){approval.proposal_source === "provided" ? " · as the client gets it" : ""}
    </a>
  );
}

export function PacketList({ approval }) {
  const docs = approval?.packet?.documents || [];
  if (!docs.length) return <span className="text-[11px] text-amber-700" data-testid="approval-packet-empty">No packet attached to this sign-off.</span>;
  const base = process.env.REACT_APP_BACKEND_URL;
  const link = (d, i) => <a href={`${base}/api/approvals/${approval.approval_id}/packet/${i}`} target="_blank" rel="noreferrer" className="text-[#003E87] font-medium hover:underline">{d.filename}</a>;
  const withIdx = docs.map((d, i) => [d, i]);
  const brief = withIdx.filter(([d]) => d.audience === "approver");
  const client = withIdx.filter(([d]) => d.audience === "client");
  const rest = withIdx.filter(([d]) => d.audience !== "approver" && d.audience !== "client");
  return (
    <div className="mt-2 space-y-2" data-testid="approval-packet">
      {approval.packet.client_line && <div className="text-xs font-semibold text-slate-800" data-testid="approval-packet-client-line">{approval.packet.client_line}</div>}
      {brief.length > 0 && <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Read first</div><ul className="mt-0.5 space-y-0.5">{brief.map(([d, i]) => <li key={i} className="text-xs" data-testid={`approval-packet-doc-${i}`}>{link(d, i)}</li>)}</ul></div>}
      {client.length > 0 && <div><div className="text-[11px] uppercase tracking-wide text-slate-500">To the client ({client.length})</div>
        <ul className="mt-0.5 space-y-0.5">{client.map(([d, i]) => <li key={i} className="text-xs" data-testid={`approval-packet-doc-${i}`}>{link(d, i)}{d.sites?.length && d.bid_name ? <span className="text-slate-400"> · {d.sites.join(", ")}</span> : null}</li>)}</ul></div>}
      {rest.length > 0 && <details className="text-xs" data-testid="approval-packet-rest"><summary className="cursor-pointer text-slate-500">For the team after award ({rest.length})</summary>
        <ul className="mt-0.5 space-y-0.5">{rest.map(([d, i]) => <li key={i} data-testid={`approval-packet-doc-${i}`}>{link(d, i)}</li>)}</ul></details>}
    </div>
  );
}

export function ExecSummaryLink({ approval, compact }) {
  const es = approval?.exec_summary;
  if (!es || es.error) return es?.error ? <span className="text-[11px] text-amber-700" data-testid="exec-summary-error">Executive summary not produced: {es.error}</span> : null;
  const f = es.facts || {};
  const m = (v) => (v == null ? "—" : `$${Math.round(v).toLocaleString()}`);
  const href = `${process.env.REACT_APP_BACKEND_URL}/api/approvals/${approval.approval_id}/exec-summary`;
  return (
    <div className={`${compact ? "mt-1" : "mt-2"} rounded-lg border border-slate-200 bg-white px-3 py-2`} data-testid="exec-summary">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#003E87] hover:underline" data-testid="exec-summary-link"><FileCheck2 className="w-3.5 h-3.5" /> Executive summary (PDF)</a>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5" data-testid="exec-summary-internal">Internal · never for the client</span>
        <span className="text-slate-400">{f.issues ? `${f.issues} issue${f.issues === 1 ? "" : "s"} to know` : "nothing flagged"}</span>
      </div>
      {!compact && f.external && (
        <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-6 gap-x-4 gap-y-1 text-[11px] text-slate-600" data-testid="exec-summary-facts-external">
          <span>Price <b className="text-slate-900">{m(f.price)}</b></span>
          <span>$/Wdc <b className="text-slate-900">{f.usd_per_wdc ? `$${f.usd_per_wdc.toFixed(4)}` : "—"}</b></span>
          <span>Money check <b className={f.money_check === "mismatch" ? "text-rose-700" : "text-slate-900"}>{f.money_check}</b></span>
          <span>Due <b className="text-slate-900">{f.due || "—"}</b></span>
          <span>Alternates <b className="text-slate-900">{f.alternates || 0}</b></span>
          <span>Open questions <b className="text-slate-900">{f.open_questions || 0}</b></span>
        </div>
      )}
      {!compact && f.money_check === "mismatch" && f.money_check_explain && (
        <div className="mt-1.5 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="exec-summary-money-explain">{f.money_check_explain}</div>
      )}
      {!compact && !f.external && (
        <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-6 gap-x-4 gap-y-1 text-[11px] text-slate-600" data-testid="exec-summary-facts">
          <span>Price <b className="text-slate-900">{m(f.price)}</b></span>
          <span>$/Wdc <b className="text-slate-900">{f.usd_per_wdc ? `$${f.usd_per_wdc.toFixed(4)}` : "—"}</b></span>
          <span>Margin <b className="text-slate-900">{f.margin_pct != null ? `${f.margin_pct.toFixed(1)}%` : "—"}</b></span>
          <span>Risk cushion <b className="text-slate-900">{f.contingency_pct != null ? `${f.contingency_pct}%` : "—"}</b></span>
          <span>Mobilize <b className="text-slate-900">{f.mobilize || "—"}</b></span>
          <span>Peak crew <b className="text-slate-900">{f.peak_people || "—"}</b></span>
        </div>
      )}
      {f.deltas && <ExecDeltas d={f.deltas} compact={compact} />}
    </div>
  );
}

function ExecDeltas({ d, compact }) {
  const mets = d.metrics || [];
  const acts = `${d.lines_changed || 0} changed · ${d.lines_added || 0} added · ${d.lines_removed || 0} removed`;
  return (
    <div className="mt-1.5 rounded-md border border-[#003E87]/20 bg-[#003E87]/5 px-2.5 py-1.5 text-[11px] text-slate-700" data-testid="exec-summary-deltas">
      <span className="font-semibold text-[#003E87]">Since round {d.round || "—"}:</span>{" "}
      {mets.length === 0 ? <span>no change in price, margin, contingency, schedule or crew</span> : mets.slice(0, compact ? 3 : 8).map((x, i) => (
        <span key={x.key} className="inline-flex items-center gap-1 mr-2" data-testid={`exec-delta-${x.key}`}>
          {i > 0 && <span className="text-slate-300">·</span>}
          <span>{x.label}</span>
          <span className="text-slate-400 line-through">{x.was}</span>
          <b className="text-slate-900">{x.now}</b>
          {x.delta && <span className={`font-mono ${x.dir === "up" ? "text-amber-700" : "text-emerald-700"}`}>{x.dir === "up" ? "▲" : "▼"} {x.delta}</span>}
        </span>
      ))}
      <span className="text-slate-400 ml-1">· activities: {acts}</span>
    </div>
  );
}

export function SignatureNudge({ onSetup }) {
  return (
    <Button size="sm" variant="outline" onClick={onSetup} data-testid="approval-signature-nudge" className="h-8 border-[#003E87]/30 text-[#003E87] hover:bg-[#003E87]/5">Set up your signature first</Button>
  );
}
